/**
 * Iconos propios en SVG (los SF Symbols de iOS no se pueden usar en la web). Todos usan `currentColor`, así que
 * toman el color del texto que los rodea, como los iconos «template» de la app nativa.
 */

const svg = (viewBox: string, body: string, cls = '') =>
  `<svg class="icon ${cls}" viewBox="${viewBox}" aria-hidden="true" focusable="false">${body}</svg>`;

/** Contorno ondulado de sello (checkmark.seal). */
function sealPath(cx: number, cy: number, r: number, bumps: number, depth: number): string {
  const pts: string[] = [];
  const n = bumps * 8;
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * Math.PI * 2;
    const rr = r - depth * (0.5 - 0.5 * Math.cos(a * bumps));
    pts.push(`${(cx + rr * Math.sin(a)).toFixed(2)},${(cy - rr * Math.cos(a)).toFixed(2)}`);
  }
  return `M${pts.join('L')}Z`;
}

/** Rueda dentada (gearshape). */
function gearPath(cx: number, cy: number, outer: number, inner: number, teeth: number): string {
  const pts: string[] = [];
  for (let i = 0; i < teeth * 4; i++) {
    const a = (i / (teeth * 4)) * Math.PI * 2;
    const r = i % 4 === 0 || i % 4 === 1 ? outer : inner;
    pts.push(`${(cx + r * Math.sin(a)).toFixed(2)},${(cy - r * Math.cos(a)).toFixed(2)}`);
  }
  return `M${pts.join('L')}Z`;
}

const SEAL = sealPath(12, 12, 11, 12, 1.6);
const CHECK = 'M7.2 12.4l3.1 3.1 6.5-6.9';

export const icons = {
  /** Flexiones (el mismo dibujo que el icono personalizado de la app nativa). */
  pushup: svg('0 0 128 104', `<g fill="none" stroke="currentColor" stroke-width="12" stroke-linecap="round" stroke-linejoin="round">
    <path d="M48 55 L112 86"/><path d="M48 55 L51 90"/><path d="M40 50 L48 55"/></g>
    <path d="M16 97 L118 97" fill="none" stroke="currentColor" stroke-width="8" stroke-linecap="round"/>
    <circle cx="31" cy="43" r="13" fill="currentColor"/>`, 'wide'),
  /** Figura en guardia de Wing Chun (figure.martial.arts). */
  martialArts: svg('0 0 24 24', `<circle cx="13.2" cy="3.6" r="2.3" fill="currentColor"/>
    <g fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12.6 7.4 L10.9 13.6"/><path d="M12.4 8.4 L20.6 8.2"/><path d="M12.2 9 L8.6 11.2 L11.9 11.6"/>
    <path d="M10.9 13.6 L6.4 16.4 L5.2 21.2"/><path d="M10.9 13.6 L15.2 16.8 L15.6 21.2"/></g>`),
  sealCheck: svg('0 0 24 24', `<path d="${SEAL}" fill="currentColor"/><path d="${CHECK}" fill="none" stroke="var(--seal-check, #fff)" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>`),
  checkCircle: svg('0 0 24 24', `<circle cx="12" cy="12" r="11" fill="currentColor"/><path d="${CHECK}" fill="none" stroke="var(--seal-check, #fff)" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>`),
  check: svg('0 0 24 24', `<path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>`),
  flame: svg('0 0 24 24', `<path fill="currentColor" d="M12.6 1.5c.6 3.2-1 5-2.6 6.8C8.3 10.2 6.5 12.1 6.5 15a5.6 5.6 0 0 0 11.2 0c0-2.4-1.1-4.1-2.1-5.4-.3 1.6-1.1 2.7-2.2 3.2.6-3.9-.1-8.3-.8-11.3z"/>`),
  gear: svg('0 0 24 24', `<path fill-rule="evenodd" fill="currentColor" d="${gearPath(12, 12, 11, 8.6, 8)} M12 8.2a3.8 3.8 0 1 0 0 7.6a3.8 3.8 0 1 0 0-7.6Z"/>`),
  chevronDown: svg('0 0 24 24', `<path d="M6 9.5l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>`),
  chevronLeft: svg('0 0 24 24', `<path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>`),
  sync: svg('0 0 24 24', `<g fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M4.5 10a7.8 7.8 0 0 1 14-3.2"/><path d="M18.9 3.2v3.9h-3.9"/><path d="M19.5 14a7.8 7.8 0 0 1-14 3.2"/><path d="M5.1 20.8v-3.9H9"/></g>`),
  warning: svg('0 0 24 24', `<path fill="currentColor" d="M10.3 3.2a2 2 0 0 1 3.4 0l8.6 15A2 2 0 0 1 20.6 21H3.4a2 2 0 0 1-1.7-2.8z"/>
    <path d="M12 9v5" stroke="var(--seal-check, #fff)" stroke-width="2.2" stroke-linecap="round"/><circle cx="12" cy="17.3" r="1.3" fill="var(--seal-check, #fff)"/>`),
  history: svg('0 0 24 24', `<g fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3.6 12a8.4 8.4 0 1 0 2.5-6"/><path d="M3.2 3.6v4.2h4.2"/><path d="M12 7.5V12l3 2"/></g>`),
  close: svg('0 0 24 24', `<path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>`),
  play: svg('0 0 24 24', `<path fill="currentColor" d="M7 4.5v15a1 1 0 0 0 1.5.9l12-7.5a1 1 0 0 0 0-1.8l-12-7.5A1 1 0 0 0 7 4.5z"/>`),
  pause: svg('0 0 24 24', `<rect x="5.5" y="4" width="4.5" height="16" rx="1.2" fill="currentColor"/><rect x="14" y="4" width="4.5" height="16" rx="1.2" fill="currentColor"/>`),
} as const;
